import { Pipe, PipeTransform } from '@angular/core';

/**
 * 🔥 Convertit une taille en octets vers l'unité la plus lisible (Ko, Mo, Go…).
 *
 * Le pipe divise successivement par 1024 jusqu'à trouver l'unité appropriée
 * parmi : `octet`, `Ko`, `Mo`, `Go`, `To`, `Po`.
 *
 * @usageNotes
 *
 * ### Syntaxe dans un template
 * ```html
 * {{ tailleEnOctets | fileSize }}          <!-- ex: "0 octet" -->
 * {{ tailleEnOctets | fileSize:2 }}        <!-- ex: "1.50 Mo"  -->
 * ```
 *
 * ### Exemples
 * | Entrée (octets) | Précision | Résultat    |
 * |-----------------|-----------|-------------|
 * | 0               | 0         | `0 octet`   |
 * | 1024            | 0         | `1 Ko`      |
 * | 1536            | 1         | `1.5 Ko`    |
 * | 1048576         | 0         | `1 Mo`      |
 * | NaN / Infinity  | —         | `?`         |
 */
@Pipe({
    name: 'fileSize',
    standalone: true,
})
export class FileSizePipe implements PipeTransform {
    /** Unités de taille de fichier, indexées par puissance de 1024 😎 */
    private readonly units = ['octet', 'Ko', 'Mo', 'Go', 'To', 'Po'];

    /**
     * Transforme une valeur en octets en chaîne lisible avec l'unité adaptée.
     *
     * @param bytes - Taille en octets (défaut : `0`)
     * @param precision - Nombre de décimales affichées (défaut : `0`)
     * @returns Chaîne formatée (ex: `"1 Ko"`) ou `"?"` si la valeur est invalide
     */
    public transform(bytes = 0, precision = 0): string {
        if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes)) {
            return '?';
        }
        let unit = 0;
        while (bytes >= 1024 && unit < this.units.length - 1) {
            bytes /= 1024;
            unit++;
        }
        return bytes.toFixed(+precision).concat(' ', this.units[unit]);
    }
}
